import React from 'react';
import { Drawer, Input, Select, Button, InputNumber } from 'antd';
import JourneyPermissionGate from './JourneyPermissionGate';
import {
  PERMISSION_TOUCHPOINTS_CONTENT_UPDATE, PERMISSION_TOUCHPOINTS_CREATE, PERMISSION_TOUCHPOINTS_DELETE, PERMISSION_TOUCHPOINTS_NAME_UPDATE,
  PERMISSION_TOUCHPOINTS_SCHEDULE_UPDATE, PERMISSION_TOUCHPOINTS_ORDER_UPDATE, PERMISSION_TOUCHPOINTS_ATTRIBUTES_UPDATE, PERMISSION_TOUCHPOINTS_LABELS_UPDATE,
} from '../../auth/permissions';
import './timeline.css';

// Controlled presentational drawer. All state/behavior comes from `editor`
// (the useTouchpointEditor return). The page owns open/close + preview + save.
export default function TouchpointEditorDrawer({ open, editor, onRequestClose, onSave, onDelete = () => {} }) {
  if (!editor) return null;
  const {
    values: f, set, posIndex, move, stepCount, labelUuids, setLabelUuids,
    canSave, isCreate, attrOptions, labelOptions, saving, linkValid,
  } = editor;

  const setInput = (k) => (e) => set(k)(e.target.value);
  const attrSelect = (key) => (
    <Select allowClear showSearch optionFilterProp="label" style={{ width: '100%' }}
      value={f[key] || undefined} onChange={(v) => set(key)(v || null)}
      placeholder="Select attribute…" options={attrOptions} />
  );
  // validation is owned by the hook (editor.linkValid); the drawer stays presentational
  const linkInvalid = f.link && !linkValid;
  const submitPermission = isCreate ? PERMISSION_TOUCHPOINTS_CREATE : PERMISSION_TOUCHPOINTS_CONTENT_UPDATE;

  return (
    <Drawer title={isCreate ? 'Add touchpoint' : 'Edit touchpoint'} placement="right" width={440}
      mask={false} visible={open} onClose={onRequestClose}
      footer={
        <div className="tp-footer">
          <div className="tp-footer-left">
            {/* Delete lives here (not on the card) and is permission-gated. Only an existing
                touchpoint can be deleted — hidden while creating one. */}
            {!isCreate && (
              <JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_DELETE}>
                <Button danger type="text" aria-label="delete touchpoint" onClick={onDelete}>Delete</Button>
              </JourneyPermissionGate>
            )}
          </div>
          <div className="tp-footer-right">
            <Button onClick={onRequestClose}>Cancel</Button>
            <JourneyPermissionGate permission={submitPermission}><Button type="primary" loading={saving} disabled={!canSave} onClick={onSave}>
              {isCreate ? 'Create touchpoint' : 'Save changes'}
            </Button></JourneyPermissionGate>
          </div>
        </div>
      }>
      <div className="tp-grp">
        <div className="tp-grp-h">Content</div>
        <label className="tp-lbl" htmlFor="tp-name">Name</label>
        <JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_NAME_UPDATE}><Input id="tp-name" aria-label="Name" value={f.name} onChange={setInput('name')} /></JourneyPermissionGate>
        <label className="tp-lbl" htmlFor="tp-msg">Message</label>
        <JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_CONTENT_UPDATE}><Input.TextArea id="tp-msg" aria-label="Message" rows={3} value={f.message} onChange={setInput('message')} /></JourneyPermissionGate>
        <label className="tp-lbl" htmlFor="tp-link">Link</label>
        <JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_CONTENT_UPDATE}><Input id="tp-link" aria-label="Link" value={f.link} onChange={setInput('link')}
          status={linkInvalid ? 'warning' : ''} /></JourneyPermissionGate>
        {linkInvalid && (
          <div className="tp-warn">⚠ Not a valid http(s) URL — this link won't be saved until fixed. Your other changes still save.</div>
        )}
      </div>

      <div className="tp-grp">
        <div className="tp-grp-h">Timing &amp; order</div>
        <label className="tp-lbl">Wait time</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_SCHEDULE_UPDATE}><InputNumber aria-label="Wait value" min={0} value={f.waitValue} onChange={(v) => set('waitValue')(v || 0)} /></JourneyPermissionGate>
          <JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_SCHEDULE_UPDATE}><Select aria-label="Wait unit" style={{ flex: 1 }} value={f.waitUnit} onChange={set('waitUnit')}
            options={[{ value: 'days', label: 'Days' }, { value: 'hours', label: 'Hours' }]} /></JourneyPermissionGate>
        </div>
        <label className="tp-lbl">Position in journey</label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span>{`Step ${posIndex + 1} of ${stepCount}`}</span>
          <JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_ORDER_UPDATE}><button type="button" className="tp-move" aria-label="Move up" disabled={posIndex <= 0} onClick={() => move(-1)}>▲ Up</button></JourneyPermissionGate>
          <JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_ORDER_UPDATE}><button type="button" className="tp-move" aria-label="Move down" disabled={posIndex >= stepCount - 1} onClick={() => move(1)}>▼ Down</button></JourneyPermissionGate>
        </div>
      </div>

      <div className="tp-grp">
        <div className="tp-grp-h">Data attributes</div>
        <label className="tp-lbl">Sent attribute</label><JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_ATTRIBUTES_UPDATE}>{attrSelect('sentAttr')}</JourneyPermissionGate>
        <label className="tp-lbl">Response attribute</label><JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_ATTRIBUTES_UPDATE}>{attrSelect('respAttr')}</JourneyPermissionGate>
        <label className="tp-lbl">Link-click attribute</label><JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_ATTRIBUTES_UPDATE}>{attrSelect('linkAttr')}</JourneyPermissionGate>
      </div>

      <div className="tp-grp">
        <div className="tp-grp-h">Labels</div>
        <JourneyPermissionGate permission={PERMISSION_TOUCHPOINTS_LABELS_UPDATE}><Select mode="multiple" aria-label="Labels" style={{ width: '100%' }}
          value={labelUuids} onChange={setLabelUuids} placeholder="Add labels…"
          optionFilterProp="label" options={labelOptions} /></JourneyPermissionGate>
      </div>
    </Drawer>
  );
}
